import { Location } from "@angular/common";
import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { OrderService } from "../../services/order/order.service";
import { ActivatedRoute, Router } from "@angular/router";
import { OrderAttributes, OrderStatus } from "@models/order.model";
import { PaymentMethod, PaymentStatus, RefundStatus } from "@models/payment.model";
import { finalize } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { AdminStatusHistoryModel } from "@models/adminStatusHistory.model";
import {
    ExchangeOrderStatusOnly,
    OrderStatusOnly,
    ReturnOrderStatusOnly,
} from "../order-status/order-status.component";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { DialogService } from "../../services/dialog/dialog.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { OrderItemAttributes } from "@models/orderItem.model";
import { ColorsPicker } from "~/src/utils/color-constants";
import { ProductCategories } from "@models/product.model";

@Component({
    selector: "app-order-info",
    templateUrl: "./order-info.component.html",
    styleUrl: "./order-info.component.scss",
    standalone: false,
})
export class OrderInfoComponent implements OnInit {
    public orderId!: string;
    public order!: OrderAttributes;
    public orderItem!: OrderItemAttributes[];
    public OrderStatus = OrderStatus;
    public PaymentMethod = PaymentMethod;
    public PaymentStatus = PaymentStatus;
    public RefundStatus = RefundStatus;
    public isLoading: boolean = false;
    public filteredOrderStatusHistory?: AdminStatusHistoryModel[] = [];
    public filteredReturnOrderStatusHistory?: AdminStatusHistoryModel[] = [];
    public filteredExchangeOrderStatusHistory?: AdminStatusHistoryModel[] = [];
    public showTimeline!: boolean;
    public isFromMail: boolean = false;
    public refundAmount!: number;
    public isTrackbuttonOn = false;
    public categories = ProductCategories;
    public recentDeliveredOrExchangedItemIds!: number[];

    @Output() dataEvent = new EventEmitter<any>();

    constructor(
        private location: Location,
        private orderService: OrderService,
        private route: ActivatedRoute,
        private router: Router,
        private dialogService: DialogService,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.route.params.subscribe((params) => {
            const from = this.route.snapshot.queryParamMap.get("from");
            if (from === "mail") {
                this.isFromMail = true;
            } else {
                this.isFromMail = false;
            }

            this.orderId = params["id"];
            if (this.orderId) {
                this.getOrderById();
            }
        });
    }

    // To get Order detail by order ID
    public getOrderById(): void {
        this.isLoading = true;
        this.orderService
            .getOrderById(this.orderId)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe({
                next: (response: OrderAttributes) => {
                    if (response) {
                        this.order = response;
                        this.orderItem = response.orderItems?.map((orderItem) => orderItem)!;
                        this.orderItem.map((item) => {
                            if (item.refund) {
                                this.refundAmount = item.refund.refundAmount;
                            }
                        });
                        this.isTrackbuttonOn =
                            this.order.paymentMethod === PaymentMethod.OnlinePayment &&
                            (this.order.paymentStatus === OrderStatus.Pending ||
                                this.order.paymentStatus === OrderStatus.Failed);

                        this.getRecentDeliveredOrExchangedItemIds();
                    }
                },
            });
    }

    public goBack(): void {
        if (window.history.length > 1) {
            this.location.back();
        } else {
            // No previous page — means open from notification
            this.router.navigate(["/home"]);
        }
    }

    /***** To get Face Type  ******/
    public getFacesByType(data: OrderItemAttributes, type: string): string[] {
        const faceArray = [];
        for (const [key, value] of Object.entries(data.singleBlockInfoDirectBuy!)) {
            if (value === type) {
                faceArray.push(key);
            }
        }
        return faceArray;
    }

    /**** To get Color Name  ****/
    public getColorValue(color: string): string | undefined {
        return ColorsPicker.find((c) => c.name === color)?.value;
    }

    /**** To get Face Color  ****/
    public getFaceColors(colorObject: object | undefined): { key: string; value: string }[] {
        if (!colorObject) {
            return [];
        }
        return Object.entries(colorObject).map(([key, value]) => ({
            key,
            value: value as string, // Ensure the value is treated as a string
        }));
    }
    public get isOrderFullyCancelledOrReturned(): boolean {
        return this.order.orderItems!.every(
            (item) => item.itemStatus === OrderStatus.Cancelled || item.itemStatus === OrderStatus.Returned,
        );
    }

    public refundStatusMessage(order: OrderAttributes): string {
        return order.orderItems!.find(
            (item) => item.refund !== null && item.refund?.refundStatus === RefundStatus.RefundSuccessful,
        )
            ? RefundStatus.RefundSuccessful
            : "";
    }

    /**** Open dialog to send help query ****/
    public openHelpDialog(): void {
        const dialogData = {
            componentName: DialogInvokingComponents.HelpMessage,
        };

        this.dialogService
            .openDialog(dialogData, "90%", { disableClose: true }, "500px")
            .afterClosed()
            .subscribe((response) => {
                if (response) {
                    this.sendHelpRequest(response.helpQuery);
                }
            });
    }
    /*** Method to send help query **/
    public sendHelpRequest(helpQuery: string): void {
        const query = helpQuery.trim();
        if (query.length > 0) {
            const orderId = +this.orderId;
            if (orderId !== undefined) {
                const message = `${query}`;
                this.orderService.sendHelpRequest(message, orderId).subscribe({
                    next: () => {
                        this.snackBar.open(
                            this.getTranslation("order.helpRequestMailSent"),
                            SnackBarConfig.Actions.CLOSE,
                            {
                                duration: SnackBarConfig.Duration.MEDIUM,
                            },
                        );
                    },
                    error: (err) => {
                        this.snackBar.open(this.getTranslation("order.failedRequest"), SnackBarConfig.Actions.CLOSE, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                    },
                });
            }
        }
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    /***  Navigate to the Order Status Timeline ***/
    public navigateOrderPage(orderID: number, itemId: number): void {
        this.router.navigate([`/account/orders/${orderID}/track`], { queryParams: { itemId: itemId } });
    }

    public navigateItemList(type: string): void {
        this.router.navigate([`/account/orders/${this.orderId}/${type}`]);
    }

    public hasExchangeItems(order: OrderAttributes): boolean {
        return order.orderItems?.some(
            (item) =>
                item.itemStatusHistory?.some(
                    (itemStatusHistory) =>
                        itemStatusHistory.status === OrderStatus.ExchangeInitiated ||
                        itemStatusHistory.status === OrderStatus.ExchangePacked ||
                        itemStatusHistory.status === OrderStatus.ExchangeDispatched ||
                        itemStatusHistory.status === OrderStatus.Exchanged,
                ),
        )!;
    }

    public hasReturnedItems(order: OrderAttributes): boolean {
        return order.orderItems?.some(
            (item) =>
                item.itemStatus === OrderStatus.ReturnInitiated ||
                item.itemStatus === OrderStatus.PickupScheduled ||
                item.itemStatus === OrderStatus.Returned,
        )!;
    }

    public isRecentDeliveredOrExchangedItem(item: OrderItemAttributes): boolean {
        return this.recentDeliveredOrExchangedItemIds?.includes(item.id!);
    }

    public getRecentDeliveredOrExchangedItemIds(): void {
        this.orderService.getRecentDeliveredOrExchangedItemIds().subscribe({
            next: (response) => {
                this.recentDeliveredOrExchangedItemIds = response;
            },
        });
    }

    private isRecentlyDeliveredOrExchangedItemExists(items: OrderItemAttributes[]): boolean {
        return items.some((item) => this.isRecentDeliveredOrExchangedItem(item));
    }

    public isReturnable(order: OrderAttributes): boolean {
        return !this.hasReturnedItems(order) && this.isRecentlyDeliveredOrExchangedItemExists(order.orderItems!);
    }

    public isExchangeable(order: OrderAttributes): boolean {
        return !this.hasExchangeItems(order) && this.isRecentlyDeliveredOrExchangedItemExists(order.orderItems!);
    }
}
